import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import InvoiceEditModal from '../../components/InvoiceEditModal';
import GenerateInvoiceModal from '../../components/GenerateInvoiceModal';
import { invoicingService } from '../../services/invoicingService';
import { format } from 'date-fns';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  // Selection state for bulk export
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Generate invoice modal state
  const [generateModalOpen, setGenerateModalOpen] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [search, statusFilter, startDate, endDate]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    setSelectedInvoiceIds([]); // Clear selection when filters change
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const data = await invoicingService.getInvoices(params);
      setInvoices(data);
    } catch (err) {
      setError('Error loading invoices: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      await invoicingService.exportInvoices(params);
      setSuccess('Invoices exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error exporting invoices: ' + (err.response?.data?.error || err.message));
    } finally {
      setExporting(false);
    }
  };

  const handleExportSelected = async () => {
    if (selectedInvoiceIds.length === 0) {
      setError('Please select at least one invoice to export');
      return;
    }

    setExporting(true);
    setError(null);
    try {
      await invoicingService.exportSelectedInvoices(selectedInvoiceIds);
      setSuccess(`${selectedInvoiceIds.length} invoice(s) exported successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error exporting invoices: ' + (err.response?.data?.error || err.message));
    } finally {
      setExporting(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedInvoiceIds(invoices.map((invoice) => invoice.id));
    } else {
      setSelectedInvoiceIds([]);
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoiceIds((prev) => {
      if (prev.includes(invoiceId)) {
        return prev.filter((id) => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const isAllSelected = invoices.length > 0 && selectedInvoiceIds.length === invoices.length;
  const isSomeSelected = selectedInvoiceIds.length > 0 && selectedInvoiceIds.length < invoices.length;

  const handleMarkPaid = async (invoiceId) => {
    if (window.confirm('Mark this invoice as paid?')) {
      try {
        await invoicingService.markInvoicePaid(invoiceId);
        setSuccess('Invoice marked as paid!');
        setTimeout(() => setSuccess(''), 3000);
        fetchInvoices();
      } catch (err) {
        setError('Error marking invoice as paid: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      await invoicingService.downloadInvoicePDF(invoiceId);
    } catch (err) {
      setError('Error viewing invoice: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      // Fetch full invoice details including line items
      const invoice = await invoicingService.getInvoice(invoiceId);
      setSelectedInvoice(invoice);
      setEditModalOpen(true);
    } catch (err) {
      setError('Error loading invoice: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleResendInvoice = async (invoiceId, customerEmail) => {
    if (!window.confirm(`Resend invoice to ${customerEmail}?`)) {
      return;
    }
    try {
      await invoicingService.resendEmail(invoiceId);
      setSuccess('Invoice resent successfully!');
      setTimeout(() => setSuccess(''), 3000);
      fetchInvoices();
    } catch (err) {
      setError('Error resending invoice: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedInvoice(null);
  };

  const handleEditModalSave = () => {
    setSuccess('Invoice updated successfully!');
    setTimeout(() => setSuccess(''), 3000);
    fetchInvoices();
  };

  const handleGenerateModalSuccess = (result) => {
    setSuccess(`Invoice ${result.invoice?.invoice_number || ''} generated successfully!`);
    setTimeout(() => setSuccess(''), 3000);
    fetchInvoices();
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      draft: {
        color: 'default',
        label: 'Draft',
        tooltip: 'Invoice created but no payment received yet',
      },
      sent: {
        color: 'info',
        label: 'Sent',
        tooltip: 'Invoice sent to customer, awaiting payment',
      },
      paid: {
        color: 'success',
        label: 'Paid',
        tooltip: 'Payment completed via Stripe',
      },
      partially_paid: {
        color: 'warning',
        label: 'Partially Paid',
        tooltip: 'Customer has made a partial payment',
      },
      overdue: {
        color: 'error',
        label: 'Overdue',
        tooltip: 'Payment is past the due date',
      },
      cancelled: {
        color: 'default',
        label: 'Cancelled',
        tooltip: 'Invoice has been cancelled',
      },
      refunded: {
        color: 'secondary',
        label: 'Refunded',
        tooltip: 'Payment has been refunded to customer',
      },
    };

    const config = statusConfig[status] || { color: 'default', label: status, tooltip: '' };
    return (
      <Tooltip title={config.tooltip} arrow>
        <Chip
          label={config.label}
          color={config.color}
          size="small"
          sx={{ cursor: 'help' }}
        />
      </Tooltip>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Invoices</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={() => setGenerateModalOpen(true)}
          >
            Generate Invoice
          </Button>
          {selectedInvoiceIds.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleExportSelected}
              disabled={exporting || loading}
            >
              {exporting ? 'Exporting...' : `Export Selected (${selectedInvoiceIds.length})`}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || loading}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Invoice #, VIN, Registration, Email, Phone"
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={handleStatusChange} label="Status">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="partially_paid">Partially Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Invoices Table */}
      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={isSomeSelected}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Technician</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Amount Due</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        No invoices found. Adjust your filters or check back later.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedInvoiceIds.includes(invoice.id)}
                          onChange={() => handleSelectInvoice(invoice.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {invoice.invoice_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                      <TableCell>
                        <Box>
                          {formatDate(invoice.due_date)}
                          {invoice.is_overdue && (
                            <Chip label="Overdue" color="error" size="small" sx={{ ml: 1 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{invoice.customer_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invoice.customer_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{invoice.vehicle_registration}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invoice.vehicle_make} {invoice.vehicle_model}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {invoice.technician_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(invoice.status)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(invoice.total_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={invoice.amount_due > 0 ? 'error.main' : 'success.main'}
                          fontWeight="medium"
                        >
                          {formatCurrency(invoice.amount_due)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title="View Invoice PDF">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewInvoice(invoice.id)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Invoice">
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => handleEditInvoice(invoice.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Resend Invoice">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleResendInvoice(invoice.id, invoice.customer_email)}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {invoice.status !== 'paid' && invoice.amount_due > 0 && (
                            <Tooltip title="Mark as Paid">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleMarkPaid(invoice.id)}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Invoice Modal */}
      <InvoiceEditModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        invoice={selectedInvoice}
        onSave={handleEditModalSave}
      />

      {/* Generate Invoice Modal */}
      <GenerateInvoiceModal
        open={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        onSuccess={handleGenerateModalSuccess}
      />
    </Box>
  );
};

export default Invoices;
